import { resolveFeaturedProducts } from '../featuredProducts';
import { resolveMediaUrl } from '../productMediaUtils';
import { normalizeHeroImageFocal } from '../heroImageFocal';
import {
  HOME_HERO_AUTOPLAY_DEFAULT_SEC,
  HOME_HERO_LAYOUT_DEFAULT,
  HOME_HERO_SLIDE_DEFAULT,
  HOME_HERO_SLIDES_MAX,
} from './heroSlidesDefaults';

function clampAutoplaySeconds(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return HOME_HERO_AUTOPLAY_DEFAULT_SEC;
  return Math.min(30, Math.max(0, Math.round(n)));
}

export function normalizeHeroSlide(item, index = 0) {
  const d = HOME_HERO_SLIDE_DEFAULT;
  const src = item && typeof item === 'object' ? item : {};
  const focal = normalizeHeroImageFocal({
    x: src.focal_x ?? src.focalX ?? d.focal_x,
    y: src.focal_y ?? src.focalY ?? d.focal_y,
  });
  const enabled =
    src.enabled === false || src.enabled === 0 || src.enabled === '0' || src.enabled === 'false'
      ? false
      : true;
  const image = String(src.image ?? '').trim();
  const link = String(src.link ?? d.link).trim() || d.link;
  const alt = String(src.alt ?? '').trim();

  return {
    id: `slide-${index}`,
    enabled,
    image,
    alt,
    link: link.startsWith('/') || link.startsWith('http') ? link : `/${link}`,
    focal_x: focal.x,
    focal_y: focal.y,
  };
}

export function normalizeHeroSlides(raw) {
  const list = Array.isArray(raw) ? raw : [];
  return list
    .slice(0, HOME_HERO_SLIDES_MAX)
    .map((item, i) => normalizeHeroSlide(item, i))
    .filter((s) => s.enabled && s.image);
}

export function normalizeHomeHeroSettings(homeCopy) {
  const src = homeCopy && typeof homeCopy === 'object' ? homeCopy : {};
  const layoutRaw = String(src.hero_layout || '').trim().toLowerCase();
  const layout = layoutRaw === 'immersive' ? 'immersive' : HOME_HERO_LAYOUT_DEFAULT;
  return {
    layout,
    autoplaySeconds: clampAutoplaySeconds(src.hero_autoplay_seconds),
    slides: normalizeHeroSlides(src.hero_slides),
  };
}

/**
 * Resolved slides for the home hero (configured slides, else OG image, else featured product).
 * Catalog fallbacks wait until site settings have loaded so a product photo cannot flash
 * before CMS hero_slides / seo_home_og_image arrive.
 */
export function resolveHeroSlides({
  homeCopy,
  seoOgImage = '',
  allProducts = [],
  featuredIds = [],
  catalogLoading = false,
  settingsReady = true,
}) {
  const settings = normalizeHomeHeroSettings(homeCopy);
  if (settings.slides.length) return { ...settings, slides: settings.slides };

  const og = String(seoOgImage || '').trim();
  if (og) {
    const focal = normalizeHeroImageFocal({
      x: homeCopy?.hero_image_focal_x,
      y: homeCopy?.hero_image_focal_y,
    });
    return {
      ...settings,
      slides: [
        normalizeHeroSlide(
          {
            image: og,
            focal_x: focal.x,
            focal_y: focal.y,
            alt: '',
            link: '/shop',
          },
          0
        ),
      ],
    };
  }

  // Avoid catalog product flash while /api/settings.php is still in flight.
  if (!settingsReady) {
    return { ...settings, slides: [] };
  }

  if (catalogLoading && allProducts.length === 0) {
    return { ...settings, slides: [] };
  }

  const featured = resolveFeaturedProducts(allProducts, featuredIds, 1);
  const img = String(featured[0]?.image || '').trim();
  if (img) {
    return {
      ...settings,
      slides: [normalizeHeroSlide({ image: img, link: '/shop' }, 0)],
    };
  }

  const withImage = (allProducts || []).find((p) => String(p?.image || '').trim());
  if (withImage?.image) {
    return {
      ...settings,
      slides: [normalizeHeroSlide({ image: withImage.image, link: '/shop' }, 0)],
    };
  }

  return { ...settings, slides: [] };
}

export function heroSlidesWithUrls(slides) {
  return (slides || []).map((s) => ({
    ...s,
    src: resolveMediaUrl(s.image),
  }));
}
